import { tt } from '@agos/shared'

import { fuelCostCents, litresUsed } from '../vehicle-fleet/fleet-math'

/**
 * The trip log — every delivery run, grouped by the vehicle that made it.
 *
 * This is the *kniha jázd*: the record a Slovak business keeps to justify fuel
 * as a business expense. It is printed and filed, which is why the page has its
 * own print stylesheet and why the totals have to reconcile with the fleet
 * page's — both read the same `fuelCostCents`, so they cannot drift.
 *
 * Ported from legacy `pages/trip-log.tsx`.
 */

export interface TripOrder {
  id?: number
  orderNumber?: string
  customerName?: string
  address?: string
  distanceKm?: number
  deliveredAt?: string | null
  status?: string
}

export interface TripRun {
  deliveryRunId?: string
  driverName?: string
  vehicleName?: string
  vehicleLicensePlate?: string
  /** Litres per 100 km, from the vehicle. */
  fuelConsumption?: number
  fuelType?: string
  /** Euros per litre — three decimals, see FB-056. */
  fuelPrice?: number
  departedAt?: string
  completedAt?: string | null
  totalDistanceKm?: number
  orderCount?: number
  orders?: TripOrder[]
}

export interface VehicleGroup {
  vehicleName: string
  vehicleLicensePlate: string
  /** Everyone who drove it in the period, in first-appearance order. */
  drivers: string[]
  runs: TripRun[]
  totalKm: number
}

/**
 * Groups runs by vehicle, keyed on the plate.
 *
 * The plate, not the name: two vehicles can share a name — a franchise with two
 * Dacias calls them both *Dacia* — and grouping by name would merge their
 * mileage into one line of a document filed for tax.
 */
export function groupByVehicle(runs: TripRun[]): VehicleGroup[] {
  const groups = new Map<string, VehicleGroup>()

  for (const run of runs) {
    const key = run.vehicleLicensePlate || run.vehicleName || ''
    let group = groups.get(key)
    if (!group) {
      group = {
        vehicleName: run.vehicleName ?? '',
        vehicleLicensePlate: run.vehicleLicensePlate ?? '',
        drivers: [],
        runs: [],
        totalKm: 0,
      }
      groups.set(key, group)
    }
    group.runs.push(run)
    group.totalKm += run.totalDistanceKm ?? 0
    const driver = run.driverName ?? ''
    if (driver && !group.drivers.includes(driver)) group.drivers.push(driver)
  }

  return [...groups.values()].sort((left, right) =>
    left.vehicleName.localeCompare(right.vehicleName, 'sk'),
  )
}

export interface TripTotals {
  runCount: number
  totalKm: number
  totalLitres: number
  /** In cents — §14.7. */
  totalCostCents: number
}

/**
 * The figures at the foot of the printed page.
 *
 * Each run is costed with `fuelCostCents` — the same function the fleet page
 * uses — and the *cents* are added, not the euros. Summing rounded euros across
 * forty runs drifts by up to twenty cents from the sum of the real costs, and
 * this document is filed.
 */
export function tripTotals(runs: TripRun[]): TripTotals {
  let totalKm = 0
  let totalLitres = 0
  let totalCostCents = 0

  for (const run of runs) {
    const vehicle = {
      fuelConsumption: run.fuelConsumption,
      fuelPrice: run.fuelPrice,
      totalDeliveryKm: run.totalDistanceKm ?? 0,
    }
    totalKm += run.totalDistanceKm ?? 0
    totalLitres += litresUsed(vehicle)
    totalCostCents += fuelCostCents(vehicle)
  }

  return { runCount: runs.length, totalKm, totalLitres, totalCostCents }
}

/** One run's own fuel cost, for the row. */
export function runCostCents(run: TripRun): number {
  return fuelCostCents({
    fuelConsumption: run.fuelConsumption,
    fuelPrice: run.fuelPrice,
    totalDeliveryKm: run.totalDistanceKm ?? 0,
  })
}

/**
 * A run with no `completedAt` is still out. It counts towards the mileage it has
 * already covered, because the kilometres were driven whether or not the driver
 * is back.
 */
export function isRunOpen(run: TripRun): boolean {
  return !run.completedAt
}

/** The vehicles that appear in the period, for the filter. */
export function vehicleOptions(runs: TripRun[]): Array<{ value: string; label: string }> {
  const seen = new Map<string, string>()
  for (const run of runs) {
    const key = run.vehicleLicensePlate || run.vehicleName || ''
    if (!key || seen.has(key)) continue
    seen.set(key, [run.vehicleName, run.vehicleLicensePlate].filter(Boolean).join(' · '))
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, 'sk'))
}

export function filterRuns(
  runs: TripRun[],
  { vehicle, search }: { vehicle: string; search: string },
): TripRun[] {
  const needle = search.trim().toLowerCase()

  return runs.filter((run) => {
    if (vehicle && (run.vehicleLicensePlate || run.vehicleName || '') !== vehicle) return false
    if (!needle) return true

    // A dispatcher searching the log is looking for a driver, a plate or a
    // customer — whichever they happen to remember.
    const haystack = [
      run.driverName,
      run.vehicleName,
      run.vehicleLicensePlate,
      ...(run.orders ?? []).flatMap((order) => [order.orderNumber, order.customerName, order.address]),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(needle)
  })
}

/**
 * The addresses a run delivered to, in the order it visited them - §9.317, the owner's Q3.
 *
 * *"we dont actually need objednávka, we need there adresy doručenia when printing to PDF."* Legacy's
 * print block joins them with an arrow (`pages/trip-log.tsx:485`) and answers an em dash for a run
 * with none; the dash here is a hyphen, per this repository's rule.
 *
 * **Blank and missing addresses are dropped rather than printed as gaps.** A run whose orders all
 * lack one is not the same as a run with no orders, and neither is auditable - so both read as `-`
 * and the count is what tells them apart on screen.
 */
export function runAddresses(run: TripRun): string[] {
  return (run.orders ?? [])
    .map((order) => (order.address ?? '').trim())
    .filter((address) => address !== '')
}

/** How a printed row says where the run went. `-` when it has nothing to say. */
export function addressLine(run: TripRun): string {
  const addresses = runAddresses(run)
  return addresses.length === 0 ? '-' : addresses.join('  →  ')
}

/** `Spolu (N jázd)` - the Slovak plural legacy gets right and the count per vehicle. */
export function runCountLabel(count: number): string {
  if (count === 1) return `${count} ${tt('jazda')}`
  if (count >= 2 && count <= 4) return `${count} ${tt('jazdy')}`
  return `${count} ${tt('jázd')}`
}
